import { FC, PropsWithChildren, useEffect, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowRight, CircleCheck, CircleX, Copy, Info } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard';
import { useSynchronizeIntegration } from '@/modules/settings';

import { IntegrationServices, IntegrationState } from '../../../../common/types/integrations';
import { connect, ConnectRequest, getAccountId } from '../../api/google-integration';
import {
  ConnectionGoogleFormValues,
  createConnectionGoogleFormSchema,
} from '../../schemas/connection-google-schema';
import { useIntegrationStore } from '../../state/use-integration-store';

interface ConnectionGoogleModalProps extends PropsWithChildren {
  /** Controlled open state (optional) */
  open?: boolean;
  /** Callback when open state changes (optional) */
  onOpenChange?: (open: boolean) => void;
}

const GOOGLE_ADMIN_CONSOLE_URL = 'https://admin.google.com';

export const ConnectionGoogleModal: FC<ConnectionGoogleModalProps> = ({
  children,
  open: controlledOpen,
  onOpenChange,
}) => {
  const { t } = useTranslation('authenticated.onboarding.integrations');
  const [_, copy] = useCopyToClipboard();
  const { setIntegrationState } = useIntegrationStore();

  const [internalOpen, setInternalOpen] = useState(false);

  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const { data: accountId, error: accountIdError } = useQuery({
    queryKey: ['googleIntegration'],
    queryFn: async () => {
      const accountId = await getAccountId();
      return String(accountId);
    },
    enabled: open,
  });

  // Handle error from query
  useEffect(() => {
    if (accountIdError) {
      toast.error(t('api.error.googleAccountIdFailed'));
    }
  }, [accountIdError, t]);

  const synchronizeMutation = useSynchronizeIntegration({
    onSuccess: () => {
      setIntegrationState(IntegrationServices.GOOGLE, IntegrationState.CONNECTED);
    },
    onError: () => {
      setIntegrationState(IntegrationServices.GOOGLE, IntegrationState.ERROR_CONNECTION);
    },
  });

  const connectMutation = useMutation({
    mutationFn: async (input: ConnectRequest) => {
      await connect(input);
    },
    onSuccess: () => {
      synchronizeMutation.mutate({ integrations: [IntegrationServices.GOOGLE] });
    },
    onError: (_: Error) => {
      setIntegrationState(IntegrationServices.GOOGLE, IntegrationState.ERROR_CONNECTION);
    },
  });

  const schema = createConnectionGoogleFormSchema(t);
  const form = useForm<ConnectionGoogleFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      clientId: '',
      oauthScopes: 'https://www.googleapis.com/auth/admin.directory.user.readonly',
      subjectEmail: '',
    },
  });

  useEffect(() => {
    if (accountId) {
      form.setValue('clientId', accountId);
    }
  }, [form, accountId]);

  const onClose = (open: boolean) => {
    form.reset();
    setOpen(open);
  };

  const handleCopy = (value: string, field: 'clientId' | 'oauthScopes') => {
    copy(value || '')
      .then(() => toast.success(t(`toast.copyToClipboard.${field}.success`)))
      .catch(() => toast.error(t(`toast.copyToClipboard.${field}.error`)));
  };

  const handleSubmit = (data: ConnectionGoogleFormValues) => {
    connectMutation.mutate({ email: data.subjectEmail });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent className="max-w-3xl gap-0 p-0">
        <DialogHeader className="space-y-1.5 border-b p-6 pb-4">
          <DialogTitle>{t('modals.connectGoogle.title')}</DialogTitle>
          <DialogDescription>{t('modals.connectGoogle.description')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)}>
            <div className="max-h-[460px] space-y-6 overflow-auto px-6 py-2">
              <div className="space-y-4 text-sm">
                <p className="mb-4 text-base font-medium">
                  {t('modals.connectGoogle.stepOne.title')}
                </p>

                <div>
                  <p>
                    {`${t('modals.connectGoogle.stepOne.goTo')} `}
                    <span className="font-medium">
                      {t('modals.connectGoogle.stepOne.googleAdminConsole')}
                    </span>
                    :
                  </p>

                  <ul className="mt-2 list-disc space-y-2 pl-6">
                    <li>
                      {`${t('modals.connectGoogle.stepOne.visit')}: `}
                      <a href={GOOGLE_ADMIN_CONSOLE_URL} target="_blank" className="underline">
                        {GOOGLE_ADMIN_CONSOLE_URL}
                      </a>
                    </li>
                    <li>
                      {`${t('modals.connectGoogle.stepOne.navigateTo')}: `}
                      <span className="font-medium">
                        {t('modals.connectGoogle.stepOne.links.security')}
                        <ArrowRight className="mx-1 inline size-4" />
                        {t('modals.connectGoogle.stepOne.links.accessAndDataControl')}
                        <ArrowRight className="mx-1 inline size-4" />
                        {t('modals.connectGoogle.stepOne.links.apiControls')}
                        <ArrowRight className="mx-1 inline size-4" />
                        {t('modals.connectGoogle.stepOne.links.domainWideDelegation')}
                      </span>
                      <br />
                      {`${t('modals.connectGoogle.stepOne.click')} `}
                      <span className="font-medium">
                        {t('modals.connectGoogle.stepOne.addNew')}
                      </span>
                      {` ${t('modals.connectGoogle.stepOne.andEnter')}:`}
                    </li>
                  </ul>
                </div>

                <FormField
                  disabled
                  control={form.control}
                  name="clientId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('fields.clientID.label')}:</FormLabel>

                      <FormControl>
                        <div className="relative">
                          <Input
                            className="truncate pr-9"
                            placeholder={t('fields.clientID.placeholder')}
                            {...field}
                          />
                          <Copy
                            className="absolute right-3 top-3 h-4 w-4 cursor-pointer transition-opacity hover:opacity-50"
                            onClick={() => handleCopy(field.value ?? '', field.name)}
                          />
                        </div>
                      </FormControl>

                      <div className="rounded-lg border border-input bg-primary-foreground p-4 text-xs">
                        <div className="flex items-center gap-1.5">
                          <Info className="size-4" />
                          {t('fields.clientID.alert')}
                        </div>
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  disabled
                  control={form.control}
                  name="oauthScopes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('fields.oauthScopes.label')}:</FormLabel>

                      <FormControl>
                        <div className="relative">
                          <Input className="truncate pr-9" {...field} />
                          <Copy
                            className="absolute right-3 top-3 h-4 w-4 cursor-pointer transition-opacity hover:opacity-50"
                            onClick={() => handleCopy(field.value ?? '', field.name)}
                          />
                        </div>
                      </FormControl>

                      <div className="rounded-lg border border-input bg-primary-foreground p-4 text-xs">
                        <div className="flex items-center gap-1.5">
                          <Info className="size-4" />
                          {t('fields.oauthScopes.alert')}
                        </div>
                      </div>
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4 text-sm">
                <div className="space-y-2">
                  <p className="text-base font-medium">{t('modals.connectGoogle.stepTwo.title')}</p>

                  <p>{t('modals.connectGoogle.stepTwo.description')}</p>
                </div>

                <FormField
                  control={form.control}
                  name="subjectEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('fields.subjectEmail.label')}</FormLabel>

                      <FormControl>
                        <Input placeholder={t('fields.subjectEmail.placeholder')} {...field} />
                      </FormControl>

                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-4 text-sm">
                <p className="text-base font-medium">{t('modals.connectGoogle.stepThree.title')}</p>

                <div className="space-y-2">
                  <p>{t('modals.connectGoogle.stepThree.description')}</p>

                  <p>{t('modals.connectGoogle.stepThree.action')}</p>
                </div>
              </div>
            </div>

            <DialogFooter className="p-6 pt-4">
              <div className="w-full space-y-6">
                {connectMutation.isSuccess && (
                  <Alert variant="success">
                    <div className="flex items-center gap-1.5">
                      <CircleCheck className="h-4 w-4 flex-shrink-0" />

                      <AlertDescription className="text-sm/4 font-medium">
                        {t('toast.successConnect')}
                      </AlertDescription>
                    </div>
                  </Alert>
                )}

                {connectMutation.isError && (
                  <Alert variant="error">
                    <div className="flex items-center gap-1.5">
                      <CircleX className="h-4 w-4 flex-shrink-0" />

                      <AlertDescription className="text-sm/4 font-medium">
                        {t('toast.errorConnect')}
                      </AlertDescription>
                    </div>
                  </Alert>
                )}

                <div className="flex w-full justify-end">
                  {connectMutation.isSuccess ? (
                    <DialogClose asChild>
                      <Button type="button">{t('actions.close')}</Button>
                    </DialogClose>
                  ) : (
                    <div className="flex items-center gap-2">
                      <DialogClose asChild>
                        <Button type="button" variant="outline">
                          {t('actions.close')}
                        </Button>
                      </DialogClose>

                      <ButtonLoadable
                        isLoading={connectMutation.isPending}
                        disabled={!form.formState.isValid}
                      >
                        {connectMutation.isPending
                          ? t('modals.connection.actions.connecting')
                          : t('actions.connect')}
                      </ButtonLoadable>
                    </div>
                  )}
                </div>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
